import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { ApiResponse } from '@/types';

const runtimeApiUrl = (): string => {
  const fromEnv = (import.meta as any).env?.VITE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.trim().length > 0) {
    return fromEnv.trim();
  }
  return '/api';
};

class ApiService {
  private api: AxiosInstance;
  public baseURL: string;

  constructor() {
    this.baseURL = runtimeApiUrl();

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = useAuthStore.getState().token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => response,
      (error) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<{data: T} | T>(url, config);
    return (response.data as any).data || response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<{data: T} | T>(url, data, config);
    return (response.data as any).data || response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<{data: T} | T>(url, data, config);
    return (response.data as any).data || response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<{data: T} | T>(url, data, config);
    return (response.data as any).data || response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<{data: T} | T>(url, config);
    return (response.data as any).data || response.data;
  }

  async upload<T = any>(url: string, file: File, config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data as T;
  }

  async download(url: string, filename?: string): Promise<void> {
    const response = await this.api.get(url, {
      responseType: 'blob',
      timeout: 300000,
    });
    this.saveBlob(response.data, filename || this.filenameFromHeaders(response) || 'download');
  }

  async downloadPost(url: string, filename?: string): Promise<string> {
    const response = await this.api.post(url, {}, {
      responseType: 'blob',
      timeout: 300000,
    });
    const resolved = filename
      || response.headers['x-backup-filename']
      || this.filenameFromHeaders(response)
      || 'backup.tar.gz';
    this.saveBlob(response.data, resolved);
    return resolved;
  }

  setToken(token: string): void {
    useAuthStore.getState().setToken(token);
  }

  private filenameFromHeaders(response: AxiosResponse): string | null {
    const header = response.headers['content-disposition'];
    if (!header || typeof header !== 'string') {
      return null;
    }
    const match = header.match(/filename="?([^"]+)"?/i);
    return match?.[1] || null;
  }

  private saveBlob(data: BlobPart, filename: string): void {
    const blob = new Blob([data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const apiService = new ApiService();
export default apiService;
